import { Component, inject, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PortalTraceService } from '@app/core/trace/portal-trace.service';
import { LoginService, TLoginState } from '@app/services/login.service';
import { environment } from '@env/environment';
import {
    checkBrowserStatus,
    getValidityClassName,
    ILoaderState,
    LoaderService,
    setAllControlsToDirty,
} from '@le_cedre/portal-lib';
import { mailPattern, noWhitespaceValidator } from '@shared/forms/validators';
import { GoogleLoginProvider, SocialAuthService } from 'angularx-social-login';
import { NgxCaptchaModule, ReCaptcha2Component } from 'ngx-captcha';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgClass, NgIf } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@le_cedre/design-system';

@Component({
    selector: 'app-portal-login-form',
    template: `
        <div class="portal-login-form-container login-container">
            <div class="portal-login__form-content" *ngIf="checkBrowserStatus() !== 'Not Supported'">
                <form (submit)="logUser()" [formGroup]="loginForm">
                    <div class="portal-login__title">
                        <span class="card-title connect-title" [innerHTML]="'CONNECT_TITLE' | translate"></span>
                    </div>

                    <div
                        class="portal-login__email form-spacing loginradius--form-element-content content-loginradius-emailid"
                        [ngClass]="getValiditySelectors('email')"
                    >
                        <label
                            class="login-email__label"
                            for="loginradius-login-emailid"
                            [innerHTML]="'FORM_LABEL_EMAIL' | translate"
                        ></label>
                        <input
                            class="login-email__input loginradius-string loginradius-emailid lr-required"
                            [class.error-field]="loginForm.get('email').errors && loginForm.dirty"
                            type="text"
                            name="emailid"
                            id="loginradius-login-emailid"
                            formControlName="email"
                        />
                        <div class="error-message" *ngIf="loginForm.get('email').errors">
                            <span
                                *ngIf="loginForm.get('email').errors.required"
                                [innerHTML]="'FORM_ERROR_REQUIRED' | translate"
                            ></span>
                            <span
                                *ngIf="loginForm.get('email').errors.pattern"
                                [innerHTML]="'FORM_ERROR_EMAIL' | translate"
                            ></span>
                        </div>
                    </div>
                    <div
                        class="portal-login__password form-spacing loginradius--form-element-content content-loginradius-password"
                    >
                        <label
                            class="login-password__label"
                            for="loginradius-login-password"
                            [innerHTML]="'FORM_LABEL_PASSWORD' | translate"
                        ></label>
                        <input
                            class="login-password__input loginradius-password loginradius-password lr-required"
                            type="password"
                            name="password"
                            id="loginradius-login-password"
                            formControlName="password"
                            [type]="hiddenPassword ? 'password' : 'text'"
                        />
                        <button
                            type="button"
                            class="portal-login__hidden_password"
                            mat-icon-button
                            (click)="hiddenPassword = !hiddenPassword"
                            [attr.aria-label]="'Hide password'"
                            [attr.aria-pressed]="hiddenPassword"
                            [disableRipple]="true"
                        >
                            <mat-icon>{{ hiddenPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                        </button>
                    </div>
                    <div class="portal-login__captcha form-spacing">
                        <ngx-recaptcha2
                            #captchaElem
                            *ngIf="displayRecaptcha"
                            [siteKey]="siteKey"
                            [size]="size"
                            [hl]="lang"
                            [theme]="theme"
                            [type]="type"
                            [useGlobalDomain]="false"
                            formControlName="recaptcha"
                            (expire)="onCaptchaExpire()"
                            (error)="onCaptchaError()"
                            (success)="onCaptchaSuccess()"
                        >
                        </ngx-recaptcha2>
                        <div class="error" *ngIf="captchaErrorKey">
                            <div class="error-message" data-cy="LOGIN_TEXT_CAPTCHA_ERROR">
                                <span [innerHTML]="captchaErrorKey | translate"></span>
                            </div>
                        </div>
                    </div>
                    <div class="portal-login__connect form-spacing">
                        <ds-button
                            class="login-button"
                            data-cy="LOGIN_BTN_SUBMIT"
                            type="submit"
                            [label]="'CONNECT' | translate"
                            [loading]="loggingSignal()"
                            aspect="primary"
                            shape="square"
                        ></ds-button>
                    </div>
                    <div class="portal-login__forgot form-spacing">
                        <a
                            [routerLink]="['/login/password/forgot']"
                            class="forgot-password-link"
                            [innerHTML]="'FORGOT_PASS' | translate"
                        ></a>
                    </div>
                    <div
                        class="portal-create__account form-spacing"
                        *ngIf="!displaySsoButton"
                        [innerHTML]="'LOGIN_CREATE_ACCOUNT_FIRST_VISITE' | translate"
                    ></div>
                    <div class="portal-login__separator form-spacing" *ngIf="displaySsoButton">
                        <span [innerHTML]="'OR' | translate" class="or-separator"></span>
                    </div>
                </form>
                <div class="portal-login__sso form-spacing" *ngIf="displaySsoButton">
                    <button type="submit" value="Login" class="submit__google-sso" (click)="signInSSO()">
                        <div class="submit__logo-google"></div>
                        <span class="submit__label-google" [innerHTML]="'GOOGLE_CONNECT' | translate"></span>
                    </button>
                </div>
                <div id="loaderLog" [ngClass]="{ 'is-spinner': isLogging, visible: isShown, hidden: !isShown }">
                    <mat-spinner *ngIf="isLogging" diameter="40"></mat-spinner>
                    <p class="loader-message" [innerHTML]="'LOADER_LOADING' | translate"></p>
                </div>
            </div>
            <div *ngIf="checkBrowserStatus() === 'Not Supported'" class="portal-login__browser-container">
                <div class="portal-login__title">
                    <span
                        class="card-title connect-title"
                        [innerHTML]="'LOGIN_BROWSER_NOT_SUPPORTED_TITLE' | translate"
                    ></span>
                </div>
                <div
                    class="portal-login__browser-text"
                    [innerHTML]="'LOGIN_BROWSER_NOT_SUPPORTED_TEXT' | translate"
                ></div>
            </div>
        </div>
    `,
    styleUrls: ['./login-form.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        NgIf,
        ReactiveFormsModule,
        NgClass,
        MatIconButton,
        MatIcon,
        NgxCaptchaModule,
        RouterLink,
        MatProgressSpinner,
        TranslateModule,
        ButtonComponent,
    ],
})
export class LoginFormComponent implements OnInit, OnDestroy {
    login = inject(LoginService);
    private trace = inject(PortalTraceService);

    login$: Observable<TLoginState>;
    @ViewChild('captchaElem') captchaElem: ReCaptcha2Component;
    loaderState$: Observable<ILoaderState>;
    isLogging = false;
    isSpinner = false;
    isShown = false;
    loginForm: UntypedFormGroup;
    loginState: TLoginState;
    public siteKey: string;
    public theme: 'light';
    public size: 'normal';
    public lang = 'fr';
    public type: 'image';
    public displayRecaptcha = false;
    public displaySsoButton = false;
    public hiddenPassword = true;
    submitted = false;
    captchaState: 'expired' | 'error' | null = null;
    destroy$ = new Subject();
    checkBrowserStatus = checkBrowserStatus;

    loggingSignal = this.login.isLogging;

    constructor(
        private activeRoute: ActivatedRoute,
        @Inject(SocialAuthService) private authService: SocialAuthService,
        private loader: LoaderService
    ) {
        this.siteKey = environment.commonOptions.recaptchaPublicKey;
        this.loaderState$ = this.loader.all$;
        this.login$ = this.login.all$();
    }

    ngOnInit() {
        this.loginForm = new UntypedFormGroup({
            email: new UntypedFormControl('', [Validators.required, noWhitespaceValidator, mailPattern]),
            password: new UntypedFormControl('', [Validators.required]),
            recaptcha: new UntypedFormControl({ disabled: true, value: '' }, [Validators.required]),
        });
        this.login$.subscribe((loginSnapshot: TLoginState) => {
            this.loginState = loginSnapshot;
            this.displayRecaptcha =
                this.loginState.currentFailedLoginAttempts >= this.loginState.maximumFailedLoginAttempts;
            if (this.displayRecaptcha && this.captchaElem) {
                this.loginForm.controls.recaptcha.enable();
                this.captchaElem.resetCaptcha();
            } else {
                this.loginForm.controls.recaptcha.disable();
            }
        });
        this.loaderState$.pipe(takeUntil(this.destroy$)).subscribe((loaderState) => {
            this.isLogging = loaderState.isLogging;
            this.isShown = loaderState.runningRequests > 0 && this.isLogging;
        });

        if (this.activeRoute.snapshot.routeConfig.path === 'collab') {
            this.displaySsoButton = true;
            this.connectAsCollab();
        }
        // get SSO token
        this.authService.authState.subscribe((user) => {
            this.login.requestLoginSSOAsCollab(user.authToken);
        });
    }

    get captchaErrorKey(): string | null {
        if (!this.displayRecaptcha) {
            return null;
        }
        if (this.captchaState === 'expired') {
            return 'FORM_ERROR_CAPTCHA_EXPIRED';
        }
        if (this.captchaState === 'error') {
            return 'FORM_ERROR_CAPTCHA_TECHNICAL';
        }
        if (this.submitted && this.loginForm.get('recaptcha')?.errors?.required) {
            return 'FORM_ERROR_CAPTCHA_REQUIRED';
        }
        return null;
    }

    onCaptchaExpire() {
        this.captchaState = 'expired';
        this.trace.trace('LOGIN', 'CAPTCHA_EXPIRED');
    }

    onCaptchaError() {
        this.captchaState = 'error';
        this.trace.trace('LOGIN', 'CAPTCHA_ERROR');
    }

    onCaptchaSuccess() {
        this.captchaState = null;
    }

    logUser() {
        this.submitted = true;
        setAllControlsToDirty(this.loginForm);
        this.loginForm.markAllAsTouched();
        if (this.loginForm.invalid) {
            const invalidControls = Object.keys(this.loginForm.controls).filter(
                (name) => this.loginForm.get(name)?.invalid
            );
            this.trace.trace('LOGIN', 'FORM_INVALID', { invalidControls });
            return;
        }
        const loginRadiusEmailOption = {
            email: this.loginForm.getRawValue().email,
            password: this.loginForm.getRawValue().password,
        };
        switch (this.activeRoute.snapshot.routeConfig.path) {
            case '':
                this.login.isLogging.set(true);
                this.login.requestLoginByEmail(loginRadiusEmailOption);
                break;
            case 'collab':
                this.login.isLogging.set(true);
                this.login.requestLoginByEmail(loginRadiusEmailOption, true);
                break;
            default:
                return;
        }
    }

    getValiditySelectors(path: string) {
        return getValidityClassName(this.loginForm, path);
    }

    connectAsCollab() {
        const { email, sessionUid, accessToken, refreshToken } = this.activeRoute.snapshot.queryParams;
        if (email && sessionUid && accessToken && refreshToken) {
            this.login.requestLoginAsCollab({ email, sessionUid, accessToken, refreshToken });
        }
    }

    signInSSO() {
        this.authService.signIn(GoogleLoginProvider.PROVIDER_ID);
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
